/**
 * <product-gallery> — mobile-first ürün galerisi.
 * - Yatay scroll-snap (CSS native, smooth)
 * - Dots + counter senkronizasyonu
 * - Tıklayınca <dialog> ile full-screen zoom (native pinch-zoom destekli)
 */
class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.viewport = this.querySelector('[data-pg-viewport]');
    this.slides = Array.from(this.querySelectorAll('[data-pg-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-pg-dot]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-pg-thumb]'));
    this.counter = this.querySelector('[data-pg-current]');
    this.zoomModal = this.querySelector('[data-pg-zoom-modal]');
    this.zoomContent = this.querySelector('[data-pg-zoom-content]');
    this.zoomClose = this.querySelector('[data-pg-zoom-close]');
    this.zoomTriggers = Array.from(this.querySelectorAll('[data-pg-zoom]'));

    this.desktopMQ = window.matchMedia('(min-width: 1024px)');

    this.onScroll = this.onScroll.bind(this);
    this.viewport?.addEventListener('scroll', this.onScroll, { passive: true });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.pgDot, 10)));
    });

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(parseInt(thumb.dataset.pgThumb, 10)));
    });

    this.zoomTriggers.forEach((trig) => {
      trig.addEventListener('click', () => this.openZoom(parseInt(trig.dataset.pgZoom, 10)));
    });

    // İlk slide aktif (desktop için zorunlu)
    this.setActive(0);

    this.zoomClose?.addEventListener('click', () => this.zoomModal?.close());
    this.zoomModal?.addEventListener('click', (e) => {
      if (e.target === this.zoomModal) this.zoomModal.close();
    });

    // Klavye desteği
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.nextSlide();
      if (e.key === 'ArrowLeft') this.prevSlide();
    });
  }

  onScroll() {
    if (!this.viewport) return;
    const slideWidth = this.viewport.offsetWidth;
    const idx = Math.round(this.viewport.scrollLeft / slideWidth);
    this.setActive(idx);
  }

  setActive(idx) {
    this.dots.forEach((d, i) => {
      const isActive = i === idx;
      d.classList.toggle('is-active', isActive);
      d.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    this.thumbs.forEach((t, i) => {
      const isActive = i === idx;
      t.classList.toggle('is-active', isActive);
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    this.slides.forEach((s, i) => {
      s.dataset.pgActive = i === idx ? 'true' : 'false';
    });
    if (this.counter) this.counter.textContent = String(idx + 1);
  }

  /**
   * Varyant seçildiğinde dışarıdan çağrılır (variant-picker → gallery).
   * Verilen media id'sine sahip slide'a geçer. Eşleşme yoksa hiçbir şey yapmaz
   * (yanlış görsele atlamaktansa mevcutta kalmak daha güvenli).
   */
  goToMedia(mediaId) {
    const target = String(mediaId);
    const idx = this.slides.findIndex((s) => s.dataset.mediaId === target);
    if (idx >= 0) this.goTo(idx);
  }

  goTo(idx) {
    if (!this.slides[idx]) return;
    if (this.desktopMQ.matches) {
      // Desktop: thumb click → anında slide swap (scroll yok)
      this.setActive(idx);
    } else if (this.viewport) {
      // Mobile: horizontal scroll
      this.viewport.scrollTo({
        left: this.slides[idx].offsetLeft,
        behavior: 'smooth',
      });
    }
  }

  nextSlide() {
    const current = this.dots.findIndex((d) => d.classList.contains('is-active'));
    this.goTo(Math.min(current + 1, this.slides.length - 1));
  }

  prevSlide() {
    const current = this.dots.findIndex((d) => d.classList.contains('is-active'));
    this.goTo(Math.max(current - 1, 0));
  }

  openZoom(idx) {
    if (!this.zoomModal || !this.zoomContent) return;
    const slide = this.slides[idx];
    const img = slide?.querySelector('img');
    if (!img) return;

    // High-res image — kaynak srcset'in en büyüğünü al
    const srcset = img.getAttribute('srcset') || '';
    const highRes = srcset.split(',').map((s) => s.trim().split(' ')[0]).pop() || img.currentSrc || img.src;

    this.zoomContent.innerHTML = `<img src="${highRes}" alt="${img.alt || ''}" style="max-width:none; touch-action:pinch-zoom;">`;
    this.zoomModal.showModal();
  }
}

customElements.define('product-gallery', ProductGallery);
